import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CourseProgressSummary,
  CourseProgressResponse,
  CourseGapProgress,
} from '@/services/courseProgress.service';

export interface CourseProgressState {
  // List of enrolled courses with summary
  enrolledCourses: CourseProgressSummary[];
  enrolledCoursesLoading: boolean;
  enrolledCoursesError: string | null;

  // Currently active course detailed progress
  activeCourseProgress: CourseProgressResponse | null;
  activeCourseLoading: boolean;
  activeCourseError: string | null;

  // Cache of course progress by courseId
  courseProgressCache: Record<string, CourseProgressResponse>;
}

const initialState: CourseProgressState = {
  enrolledCourses: [],
  enrolledCoursesLoading: false,
  enrolledCoursesError: null,

  activeCourseProgress: null,
  activeCourseLoading: false,
  activeCourseError: null,

  courseProgressCache: {},
};

const courseProgressSlice = createSlice({
  name: 'courseProgress',
  initialState,
  reducers: {
    // Enrolled courses list
    setEnrolledCoursesLoading: (state, action: PayloadAction<boolean>) => {
      state.enrolledCoursesLoading = action.payload;
      if (action.payload) {
        state.enrolledCoursesError = null;
      }
    },
    setEnrolledCourses: (state, action: PayloadAction<CourseProgressSummary[]>) => {
      state.enrolledCourses = action.payload;
      state.enrolledCoursesLoading = false;
      state.enrolledCoursesError = null;
    },
    setEnrolledCoursesError: (state, action: PayloadAction<string>) => {
      state.enrolledCoursesError = action.payload;
      state.enrolledCoursesLoading = false;
    },

    // Active course progress
    setActiveCourseLoading: (state, action: PayloadAction<boolean>) => {
      state.activeCourseLoading = action.payload;
      if (action.payload) {
        state.activeCourseError = null;
      }
    },
    setActiveCourseProgress: (state, action: PayloadAction<CourseProgressResponse | null>) => {
      state.activeCourseProgress = action.payload;
      state.activeCourseLoading = false;
      state.activeCourseError = null;

      // Also cache it
      if (action.payload) {
        state.courseProgressCache[action.payload.courseId] = action.payload;
      }
    },
    setActiveCourseError: (state, action: PayloadAction<string>) => {
      state.activeCourseError = action.payload;
      state.activeCourseLoading = false;
    },
    clearActiveCourseProgress: (state) => {
      state.activeCourseProgress = null;
      state.activeCourseLoading = false;
      state.activeCourseError = null;
    },

    // Update progress from quiz attempt
    updateCourseProgressFromAttempt: (state, action: PayloadAction<CourseProgressResponse>) => {
      state.activeCourseProgress = action.payload;
      state.courseProgressCache[action.payload.courseId] = action.payload;

      // Update the summary in enrolledCourses list
      const index = state.enrolledCourses.findIndex(c => c.courseId === action.payload.courseId);
      if (index !== -1) {
        state.enrolledCourses[index] = {
          ...state.enrolledCourses[index],
          overallProgress: action.payload.overallProgress,
          totalPoints: action.payload.totalPoints,
          discoveredGapsCount: action.payload.discoveredGaps.length,
          completedQuizzesCount: action.payload.completedQuizzes.length,
          lastActivityAt: action.payload.lastActivityAt,
        };
      }
    },

    // Update module progress
    updateModuleProgress: (state, action: PayloadAction<{
      courseId: string;
      moduleId: string;
      progress: number;
      completed?: boolean;
    }>) => {
      const { courseId, moduleId, progress, completed } = action.payload;

      // Update active course progress if it matches
      if (state.activeCourseProgress?.courseId === courseId) {
        state.activeCourseProgress.moduleProgress[moduleId] = {
          ...state.activeCourseProgress.moduleProgress[moduleId],
          progress,
          completedAt: completed ? new Date().toISOString() : undefined,
        };
      }

      // Update cache
      if (state.courseProgressCache[courseId]) {
        state.courseProgressCache[courseId].moduleProgress[moduleId] = {
          ...state.courseProgressCache[courseId].moduleProgress[moduleId],
          progress,
          completedAt: completed ? new Date().toISOString() : undefined,
        };
      }
    },

    // Update stakeholder trust level
    updateTrustLevel: (state, action: PayloadAction<{
      courseId: string;
      stakeholderId: string;
      trustLevel: number;
    }>) => {
      const { courseId, stakeholderId, trustLevel } = action.payload;

      // Update active course progress if it matches
      if (state.activeCourseProgress?.courseId === courseId) {
        state.activeCourseProgress.stakeholderTrustLevels[stakeholderId] = trustLevel;
      }

      // Update cache
      if (state.courseProgressCache[courseId]) {
        state.courseProgressCache[courseId].stakeholderTrustLevels[stakeholderId] = trustLevel;
      }
    },

    // Add discovered gap
    addDiscoveredGap: (state, action: PayloadAction<{
      courseId: string;
      gapId: string;
      gapProgress: CourseGapProgress;
    }>) => {
      const { courseId, gapId, gapProgress } = action.payload;

      // Update active course progress if it matches
      if (state.activeCourseProgress?.courseId === courseId) {
        if (!state.activeCourseProgress.discoveredGaps.includes(gapId)) {
          state.activeCourseProgress.discoveredGaps.push(gapId);
        }
        state.activeCourseProgress.gaps[gapId] = gapProgress;
      }

      // Update cache
      if (state.courseProgressCache[courseId]) {
        if (!state.courseProgressCache[courseId].discoveredGaps.includes(gapId)) {
          state.courseProgressCache[courseId].discoveredGaps.push(gapId);
        }
        state.courseProgressCache[courseId].gaps[gapId] = gapProgress;
      }

      // Update enrolled courses count
      const index = state.enrolledCourses.findIndex(c => c.courseId === courseId);
      if (index !== -1) {
        state.enrolledCourses[index].discoveredGapsCount += 1;
      }
    },

    // Reset course progress (after backend reset)
    resetCourseProgress: (state, action: PayloadAction<string>) => {
      const courseId = action.payload;

      // Clear active progress if it matches
      if (state.activeCourseProgress?.courseId === courseId) {
        state.activeCourseProgress = null;
      }

      // Clear from cache
      delete state.courseProgressCache[courseId];

      // Reset summary in enrolled courses
      const index = state.enrolledCourses.findIndex(c => c.courseId === courseId);
      if (index !== -1) {
        state.enrolledCourses[index] = {
          ...state.enrolledCourses[index],
          overallProgress: 0,
          totalPoints: 0,
          discoveredGapsCount: 0,
          completedQuizzesCount: 0,
        };
      }
    },

    // Clear all state (logout)
    clearCourseProgressState: () => initialState,
  },
});

export const {
  setEnrolledCoursesLoading,
  setEnrolledCourses,
  setEnrolledCoursesError,
  setActiveCourseLoading,
  setActiveCourseProgress,
  setActiveCourseError,
  clearActiveCourseProgress,
  updateCourseProgressFromAttempt,
  updateModuleProgress,
  updateTrustLevel,
  addDiscoveredGap,
  resetCourseProgress,
  clearCourseProgressState,
} = courseProgressSlice.actions;

export default courseProgressSlice.reducer;
